// Brand settings > Google Search Console: connect a brand-owned domain so
// Consumer Voice analyses can read first-party search demand for the
// brand's product pages. Flow: add domain -> we show the TXT record our
// Google service account issued -> the brand adds it at their DNS host ->
// Verify. Google can take hours to see a new record, so Verify is safe to
// repeat; a `revoked` row means the record was removed later.
import React, { useCallback, useEffect, useState } from 'react';
import { Button, TextField } from '@mui/material';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import {
  brandSearchConsoleEndpoint,
  brandSearchConsolePropertyEndpoint,
  brandSearchConsoleVerifyEndpoint,
} from '../../../api/endpoints';
import { useUser } from '../../../hooks/useUser';
import { useWorkspaceBrandId } from '../../../hooks/useWorkspaceBrandId';

interface ScProperty {
  id: number;
  domain: string;
  status: 'pending' | 'verified' | 'failed' | 'revoked';
  verified_at: string | null;
  last_checked_at: string | null;
  last_error: string | null;
  service_account_email: string | null;
  instructions: { record_type: string; host: string; name: string; value: string; ttl_hint: string } | null;
}

const STATUS_STYLE: Record<ScProperty['status'], string> = {
  verified: 'bg-[#E8F5E9] text-[#2E7D32]',
  pending: 'bg-tint text-accent-text',
  failed: 'bg-[#FBEFEF] text-[#c62828]',
  revoked: 'bg-[#FBEFEF] text-[#c62828]',
};

const STATUS_LABEL: Record<ScProperty['status'], string> = {
  verified: 'Connected',
  pending: 'Waiting for DNS record',
  failed: 'Verification failed',
  revoked: 'Access revoked',
};

const ERROR_COPY: Record<string, string> = {
  TokenNotFound: "Google can't see the TXT record yet. Check the value and try again in a few hours.",
  SearchConsoleRevoked: 'The TXT record was removed, so Google withdrew our access. Add it back and verify again.',
  SiteVerificationForbidden: "Google refused the verification request. We'll look into it.",
  Quota: 'Google is rate-limiting verification checks. Try again later.',
};

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the value and copy it manually');
    }
  };
  return (
    <div className="flex items-start gap-2">
      <code className="flex-1 min-w-0 break-all text-[12px] bg-[#333] text-white rounded-md px-3 py-2">{value}</code>
      <Button size="small" variant="outlined" onClick={copy}>
        {copied ? 'Copied!' : 'Copy'}
      </Button>
    </div>
  );
}

export default function SearchConsole({ brandId: brandIdProp = 0 }: { brandId?: number } = {}) {
  const { brand } = useUser();
  const workspaceBrandId = useWorkspaceBrandId();
  const brandId = brandIdProp || workspaceBrandId || (brand && brand[0] && brand[0].id) || 0;
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [rows, setRows] = useState<ScProperty[]>([]);
  const [domain, setDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    try {
      const { data } = await axiosInstant.get(brandSearchConsoleEndpoint(brandId));
      setConfigured(data.configured !== false);
      setRows(data.properties || []);
    } catch {
      toast.error("Couldn't load the Search Console connections.");
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!domain.trim()) return;
    setAdding(true);
    try {
      const { data } = await axiosInstant.post(brandSearchConsoleEndpoint(brandId), { domain: domain.trim() });
      setRows((prev) => [...prev.filter((r) => r.id !== data.id), data]);
      setDomain('');
      toast.success('Domain added — add the TXT record below, then verify.');
    } catch (e: any) {
      const status = e?.response?.status;
      const code = e?.response?.data?.error;
      if (status === 400) toast.error('Enter a domain like example.com (no https:// or path).');
      else if (status === 503) toast.error('Search Console is not configured on this environment yet.');
      else if (code === 'DomainClaimedByAnotherBrand') toast.error('Another brand has already connected this domain. Contact support if it belongs to you.');
      else toast.error(`Couldn't add the domain${code ? ` (${code})` : ''}.`);
    } finally {
      setAdding(false);
    }
  };

  const verify = async (row: ScProperty) => {
    setBusyId(row.id);
    try {
      const { data } = await axiosInstant.post(brandSearchConsoleVerifyEndpoint(brandId, row.id));
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...data } : r)));
      if (data.status === 'verified') toast.success(`${row.domain} is connected.`);
      else if (data.status === 'pending') {
        toast(
          data.txt_found_locally
            ? "We can see the record; Google can't yet. Try again in a few hours."
            : "The TXT record isn't visible yet. Check the value at your DNS host and try again later.",
        );
      } else toast.error(ERROR_COPY[data.last_error] || `Verification failed${data.last_error ? ` (${data.last_error})` : ''}.`);
    } catch (e: any) {
      if (e?.response?.status === 429) toast('Give it a minute before verifying again.');
      else if (e?.response?.data?.error === 'DomainClaimedByAnotherBrand') toast.error('Another brand has already connected this domain. Contact support if it belongs to you.');
      else toast.error("Couldn't run the verification. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row: ScProperty) => {
    if (!window.confirm(`Disconnect ${row.domain}? Consumer Voice analyses will stop reading its Search Console data.`)) return;
    setBusyId(row.id);
    try {
      await axiosInstant.delete(brandSearchConsolePropertyEndpoint(brandId, row.id));
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(`${row.domain} disconnected.`);
    } catch {
      toast.error("Couldn't disconnect the domain.");
    } finally {
      setBusyId(null);
    }
  };

  if (!brandId) return null;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h3 className="text-[16px] font-bold text-text-1">Google Search Console</h3>
        <p className="text-[13px] text-text-2 mt-1 max-w-[720px]">
          Connect the domains you own so Consumer Voice analyses can read how shoppers find your product pages on
          Google (impressions, clicks, the exact queries). Retailer pages are never included. We ask Google for a
          verification record, you add it at your DNS host, and Google grants our service account read access to
          the whole domain. No Google login or password is shared.
        </p>
        {!configured ? (
          <p className="text-[12px] text-[#c62828] mt-2">Search Console is not configured on this environment yet.</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <TextField
          size="small"
          label="Domain"
          placeholder="example.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add();
          }}
          disabled={adding || !configured}
          sx={{ minWidth: 280 }}
        />
        <Button variant="contained" disableElevation onClick={add} disabled={adding || !configured || !domain.trim()}>
          {adding ? 'Adding…' : 'Add domain'}
        </Button>
      </div>

      {loading ? <p className="text-[12px] text-muted">Loading…</p> : null}
      {!loading && rows.length === 0 ? <p className="text-[12px] text-muted">No domains connected yet.</p> : null}
      {!loading && rows.length > 0 ? (
        <div className="flex flex-col gap-4">
          {rows.map((row) => (
            <div key={row.id} className="border border-border-token rounded-xl p-4 bg-surface">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-text-1 text-[14px] truncate">{row.domain}</span>
                  <span className={`text-[11px] font-bold uppercase rounded-full px-2 py-0.5 ${STATUS_STYLE[row.status]}`}>
                    {STATUS_LABEL[row.status]}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {row.status !== 'verified' ? (
                    <Button size="small" variant="contained" disableElevation onClick={() => verify(row)} disabled={busyId === row.id || !configured}>
                      {busyId === row.id ? 'Checking…' : 'Verify'}
                    </Button>
                  ) : null}
                  <Button size="small" variant="text" color="inherit" onClick={() => remove(row)} disabled={busyId === row.id}>
                    Disconnect
                  </Button>
                </div>
              </div>
              {row.status === 'verified' ? (
                <p className="text-[12px] text-text-2 mt-2">
                  Connected{row.verified_at ? ` on ${new Date(row.verified_at).toLocaleDateString()}` : ''}. Consumer Voice analyses of
                  products on this domain now include Search Console demand.
                </p>
              ) : null}
              {row.status !== 'verified' && row.instructions ? (
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-[12px] text-text-2">
                    Add this <strong>TXT</strong> record at your DNS host for <strong>{row.instructions.name}</strong> (host{' '}
                    <code>{row.instructions.host}</code>), then click Verify. {row.instructions.ttl_hint}
                  </p>
                  <CopyValue value={row.instructions.value} />
                  {row.last_error ? (
                    <p className="text-[12px] text-[#A0662E] dark:text-accent-text">
                      {ERROR_COPY[row.last_error] || row.last_error}
                      {row.last_checked_at ? ` (last checked ${new Date(row.last_checked_at).toLocaleString()})` : ''}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
